import { defineStore } from 'pinia'
import MyPortalService from '../services/myPortalService.js'
import {
  buildBookingPayload,
  buildGuestPayload,
  buildGuestUpdatePayload,
  buildCheckInDateTime,
  buildCheckOutDateTime,
  getApartmentId,
  normalizeEmail,
  validateBookingRequest,
  categorizeBookingStatus
} from '../lib/booking.js'
import {
  isBlockingBookingStatus,
  isNonBlockingBookingStatus,
  bookingRangesOverlap
} from '../config/bookingAvailability.js'
import { sendContactMessage } from '../lib/contactMail.js'
import { hasDisplayText } from '../lib/helper.js'

const LEGAL_CONTENT_IDENTIFIERS = new Set(['imprint', 'data_protection', 'terms'])

const TERMS_SECTIONS = [
  { field: 'arrival_departure', title: '1. Anreise / Abreise' },
  { field: 'special_requests', title: '2. Sonderwünsche und Nebenabreden' },
  { field: 'payment', title: '3. Bezahlung' },
  { field: 'cancellation', title: '4. Rücktritt' },
  { field: 'tenant_obligations', title: '5. Pflichten des Mieters' },
  { field: 'data_protection_section', title: '6. Datenschutz' },
  { field: 'liability', title: '7. Haftung' },
  { field: 'final_provisions', title: '8. Schlussbestimmungen' },
]

function buildTermsContent(record) {
  if (!record) return ''

  if (hasDisplayText(record.content)) {
    return String(record.content).trim()
  }

  return TERMS_SECTIONS
    .filter(({ field }) => hasDisplayText(record[field]))
    .map(({ field, title }) => `<h3>${title}</h3>${String(record[field]).trim()}`)
    .join('')
}

function resolveLegalContentItem(record, identifier, fallbackTitle) {
  if (!record) return null

  const content =
    identifier === 'terms' ? buildTermsContent(record) : String(record.content || '').trim()

  if (!hasDisplayText(content)) return null

  return {
    identifier,
    title: record.title || fallbackTitle,
    content,
  }
}

function pickLegalContentItem(records, identifier, fallbackTitle) {
  if (!Array.isArray(records) || !records.length) return null

  const matches = records
    .map((record) => ({
      record,
      item: resolveLegalContentItem(record, identifier, fallbackTitle),
    }))
    .filter((entry) => entry.item)
    .sort(
      (a, b) =>
        new Date(b.record.updatedAt || 0).getTime() -
        new Date(a.record.updatedAt || 0).getTime()
    )

  return matches[0]?.item || null
}

/**
 * Zentraler API Store mit modularer Struktur
 * Verwaltet alle API-Daten, Loading-States und Errors einheitlich
 */
export const useApiStore = defineStore('api', {
  state: () => ({
    // Daten-Module
    data: {
      apartments: { items: [], loading: false, error: null, lastUpdated: null },
      prices: { items: [], loading: false, error: null, lastUpdated: null },
      reviews: { items: [], loading: false, error: null, lastUpdated: null },
      about: { item: {}, loading: false, error: null, lastUpdated: null },
      notices: { item: {}, loading: false, error: null, lastUpdated: null },
      content: { items: [], loading: false, error: null, lastUpdated: null },
      impressions: { items: [], loading: false, error: null, lastUpdated: null },
      gallery: { items: [], loading: false, error: null, lastUpdated: null },
      sections: { items: [], loading: false, error: null, lastUpdated: null },
      parallaxImages: { items: [], loading: false, error: null, lastUpdated: null },
      backgroundImages: { items: [], loading: false, error: null, lastUpdated: null },
      contact: { item: {}, loading: false, error: null, lastUpdated: null },
      availability: { items: [], loading: false, error: null, lastUpdated: null },
      openingTimes: { items: [], loading: false, error: null, lastUpdated: null },
      meta: { items: [], loading: false, error: null, lastUpdated: null }
    },
    
    // Globale States
    globalLoading: false,
    initialized: false,
    availabilityRequestToken: 0,
    
    // API Service Instance
    apiService: null,
    myportalClient: null
  }),

  getters: {
    // Globale Getters
    isGlobalLoading: (state) => state.globalLoading,
    isInitialized: (state) => state.initialized,
    hasAnyErrors: (state) => {
      return Object.values(state.data).some(module => module.error !== null)
    },
    
    // Apartments Getters
    apartments: (state) => state.data.apartments.items,
    apartmentsLoading: (state) => state.data.apartments.loading,
    apartmentsError: (state) => state.data.apartments.error,
    hasApartments: (state) => state.data.apartments.items.length > 0,
    getApartmentBySlug: (state) => (slug) => {
      return state.data.apartments.items.find(apt => apt.slug === slug) || null
    },
    getApartmentById: (state) => (id) => {
      return state.data.apartments.items.find(apt => apt._id === id) || null
    },

    // Prices Getters
    prices: (state) => state.data.prices.items,
    pricesLoading: (state) => state.data.prices.loading,
    pricesError: (state) => state.data.prices.error,
    hasPrices: (state) => state.data.prices.items.length > 0,
    getPricesByApartmentSlug: (state) => (slug) => {
      return state.data.prices.items.filter(price => 
        price.apartment && price.apartment.slug === slug
      )
    },
    getPricesByApartmentId: (state) => (apartmentId) => {
      return state.data.prices.items.filter(price => 
        price.vacation_rental_id === apartmentId || 
        price.apartment_id === apartmentId
      )
    },

    // Reviews Getters
    reviews: (state) => state.data.reviews.items,
    reviewsLoading: (state) => state.data.reviews.loading,
    reviewsError: (state) => state.data.reviews.error,
    hasReviews: (state) => state.data.reviews.items.length > 0,
    getReviewsByApartmentSlug: (state) => (slug) => {
      const apartment = state.data.apartments.items.find((apt) => apt.slug === slug)
      const apartmentId = apartment?._id != null ? String(apartment._id) : ''

      return state.data.reviews.items.filter((review) => {
        if (review.vacation_rental?.slug === slug) return true
        if (apartmentId && review.vacation_rental_id != null) {
          return String(review.vacation_rental_id) === apartmentId
        }
        return false
      })
    },
    getReviewsByApartmentId: (state) => (apartmentId) => {
      return state.data.reviews.items.filter(review => 
        review.vacation_rental_id === apartmentId
      )
    },

    // About Getters
    about: (state) => state.data.about.item,
    aboutLoading: (state) => state.data.about.loading,
    aboutError: (state) => state.data.about.error,
    hasAbout: (state) => Object.keys(state.data.about.item).length > 0,


    // Notices Getters
    notices: (state) => state.data.notices.item,
    noticesLoading: (state) => state.data.notices.loading,
    noticesError: (state) => state.data.notices.error,
    hasNotices: (state) => Object.keys(state.data.notices.item).length > 0,

    // Contact Getters
    contact: (state) => state.data.contact.item,
    contactLoading: (state) => state.data.contact.loading,
    contactError: (state) => state.data.contact.error,
    hasContact: (state) => Object.keys(state.data.contact.item).length > 0,

    // Availability Getters
    availability: (state) => state.data.availability.items,
    availabilityLoading: (state) => state.data.availability.loading,
    availabilityError: (state) => state.data.availability.error,
    hasAvailability: (state) => state.data.availability.items.length > 0,

    // Content Getters
    content: (state) => state.data.content.items,
    contentLoading: (state) => state.data.content.loading,
    contentError: (state) => state.data.content.error,
    hasContent: (state) => state.data.content.items.length > 0,
    getContentByIdentifier: (state) => (identifier) => {
      const matches = state.data.content.items.filter(
        (data) => data.identifier === identifier
      )
      return matches.length ? matches[matches.length - 1] : {}
    },

    // Impressions Getters
    impressions: (state) => state.data.impressions.items,
    impressionsLoading: (state) => state.data.impressions.loading,
    impressionsError: (state) => state.data.impressions.error,
    hasImpressions: (state) => state.data.impressions.items.length > 0,

    // Gallery Getters
    gallery: (state) => state.data.gallery.items,
    galleryLoading: (state) => state.data.gallery.loading,
    galleryError: (state) => state.data.gallery.error,
    hasGallery: (state) => state.data.gallery.items.length > 0,
    getGalleryByApartmentSlug: (state) => (slug) => {
      return state.data.gallery.items.filter(item => 
        item.vacation_rental && item.vacation_rental.slug === slug
      )
    },
    getGalleryByApartmentId: (state) => (apartmentId) => {
      return state.data.gallery.items.filter(item => 
        item.vacation_rental_id === apartmentId
      )
    },

    // Sections Getters
    sections: (state) => state.data.sections.items,
    sectionsLoading: (state) => state.data.sections.loading,
    sectionsError: (state) => state.data.sections.error,
    hasSections: (state) => state.data.sections.items.length > 0,
    getSectionByKey: (state) => (key) => {
      return state.data.sections.items.find(section => section.section_key === key) || null
    },

    // Parallax Images Getters
    parallaxImages: (state) => state.data.parallaxImages.items,
    parallaxImagesSorted: (state) => [...state.data.parallaxImages.items].sort((a, b) => a.sort_order - b.sort_order),
    parallaxImagesLoading: (state) => state.data.parallaxImages.loading,
    parallaxImagesError: (state) => state.data.parallaxImages.error,
    hasParallaxImages: (state) => state.data.parallaxImages.items.length > 0,
    getParallaxImageBySortOrder: (state) => (sortOrder) => {
      return state.data.parallaxImages.items.find(
        (img) => Number(img.sort_order) === Number(sortOrder)
      ) || null
    },

    // Background Images Getters
    backgroundImages: (state) => state.data.backgroundImages.items,
    backgroundImagesLoading: (state) => state.data.backgroundImages.loading,
    backgroundImagesError: (state) => state.data.backgroundImages.error,
    hasBackgroundImages: (state) => state.data.backgroundImages.items.length > 0,
    getBackgroundImageByIdentifier: (state) => (identifier) => {
      const result = state.data.backgroundImages.items.filter(
        (data) => data.identifier === identifier
      )
      return result.length ? result[0] : {}
    }
  },

  actions: {
    /**
     * Initialisiert den API Service
     */
    initializeApiService(myportalClient) {
      if (!this.apiService) {
        this.apiService = new MyPortalService(myportalClient)
      }
      this.myportalClient = myportalClient
    },

    /**
     * Setzt Loading-State für ein Modul
     */
    _setModuleLoading(moduleName, loading) {
      if (this.data[moduleName]) {
        this.data[moduleName].loading = loading
      }
    },

    /**
     * Setzt Error für ein Modul
     */
    _setModuleError(moduleName, error) {
      if (this.data[moduleName]) {
        this.data[moduleName].error = error
        this.data[moduleName].lastUpdated = Date.now()
      }
    },

    /**
     * Setzt Daten für ein Modul
     */
    _setModuleData(moduleName, data, isCollection = true) {
      if (this.data[moduleName]) {
        if (isCollection) {
          this.data[moduleName].items = data
        } else {
          this.data[moduleName].item = data
        }
        this.data[moduleName].error = null
        this.data[moduleName].lastUpdated = Date.now()
      }
    },

    /**
     * Lädt alle kritischen Daten parallel
     */
    async loadInitialData() {
      if (!this.apiService) {
        throw new Error('API Service not initialized')
      }

      this.globalLoading = true
      
      try {
        const results = await Promise.allSettled([
          this.loadApartments(),
          this.loadPrices(),
          this.loadReviews(),
          this.loadAbout(),
          this.loadNotices(),
          this.loadContact(),
          this.loadBackgroundImages(),
          this.loadImpressions(),
          this.loadGallery(),
          this.loadSections(),
          this.loadParallaxImages()
        ])

        const failed = results.filter((result) => result.status === 'rejected')
        if (failed.length > 0) {
          console.warn(
            '[API Store] Some initial data modules failed to load:',
            failed.map((result) => result.reason?.message || result.reason)
          )
        }

        this.initialized = true
      } catch (error) {
        console.error('Failed to load initial data:', error)
        throw error
      } finally {
        this.globalLoading = false
      }
    },

    /**
     * Lädt Apartments
     */
    async loadApartments() {
      this._setModuleLoading('apartments', true)
      
      try {
        const result = await this.apiService.getCollection('vacation_rentals')
        const apartments = result.data || []

        let furnishingsByRental = new Map()
        try {
          const amenitiesResult = await this.apiService.getCollection('amenities', {
            limit: 200,
            order: { sort_order: 1 }
          })

          furnishingsByRental = (amenitiesResult.data || []).reduce((map, entry) => {
            const rentalId = entry.vacation_rental_id
            if (!rentalId) return map
            if (!map.has(rentalId)) map.set(rentalId, [])
            map.get(rentalId).push({
              id: entry._id,
              furnishings_id: {
                icon: entry.icon,
                label: entry.feature
              }
            })
            return map
          }, new Map())
        } catch (amenitiesError) {
          console.warn('[API Store] Amenities unavailable:', amenitiesError.message)
        }

        const enriched = apartments.map((apartment) => ({
          ...apartment,
          furnishings: furnishingsByRental.get(apartment._id) || []
        }))

        this._setModuleData('apartments', enriched, true)
        return { ...result, data: enriched }
      } catch (error) {
        this._setModuleError('apartments', error.message)
        throw error
      } finally {
        this._setModuleLoading('apartments', false)
      }
    },

    /**
     * Lädt Preise mit Relations
     */
    async loadPrices() {
      this._setModuleLoading('prices', true)
      
      try {
        const result = await this.apiService.getWithRelations('prices', [
          { 
            field: 'vacation_rental_id', 
            from: 'vacation_rentals', 
            as: 'apartment' 
          }
        ])
        this._setModuleData('prices', result.data, true)
        return result
      } catch (error) {
        this._setModuleError('prices', error.message)
        throw error
      } finally {
        this._setModuleLoading('prices', false)
      }
    },

    /**
     * Load reviews with apartment relations
     */
    async loadReviews() {
      this._setModuleLoading('reviews', true)

      const relationConfig = [
        {
          field: 'vacation_rental_id',
          from: 'vacation_rentals',
          as: 'vacation_rental'
        }
      ]
      const options = {
        filter: JSON.stringify({ published: true }),
        limit: 100,
        sort: { date: -1, createdAt: -1 }
      }

      try {
        let result

        try {
          result = await this.apiService.getWithRelations('reviews', relationConfig, options)
        } catch (relationError) {
          console.warn('[API Store] Reviews relations unavailable, using collection fallback:', relationError.message)
          result = await this.apiService.getCollection('reviews', options)
        }

        this._setModuleData('reviews', result.data || [], true)
        return result
      } catch (error) {
        this._setModuleError('reviews', error.message)
        console.error('[API Store] Failed to load reviews:', error)
        return { data: [], success: false }
      } finally {
        this._setModuleLoading('reviews', false)
      }
    },

    /**
     * Lädt About (Singleton)
     */
    async loadAbout() {
      this._setModuleLoading('about', true)
      
      try {
        const result = await this.apiService.getSingleton('about', 'about')
        this._setModuleData('about', result.data, false)
        return result
      } catch (error) {
        this._setModuleError('about', error.message)
        throw error
      } finally {
        this._setModuleLoading('about', false)
      }
    },


    /**
     * Lädt Notices (Singleton)
     */
    async loadNotices() {
      this._setModuleLoading('notices', true)
      
      try {
        const result = await this.apiService.getSingleton('notices', 'notices')
        this._setModuleData('notices', result.data, false)
        return result
      } catch (error) {
        this._setModuleError('notices', error.message)
        throw error
      } finally {
        this._setModuleLoading('notices', false)
      }
    },

    /**
     * Lädt Contact (Singleton)
     */
    async loadContact() {
      this._setModuleLoading('contact', true)
      
      try {
        const result = await this.apiService.getSingleton('contact', 'contact')
        this._setModuleData('contact', result.data, false)
        return result
      } catch (error) {
        this._setModuleError('contact', error.message)
        throw error
      } finally {
        this._setModuleLoading('contact', false)
      }
    },

    /**
     * Lädt Content inkl. Impressum/Datenschutz aus eigenen MyPortal-Tabellen.
     * @param {{ force?: boolean }} options force=true leert API-Cache und lädt neu
     */
    async loadContent(options = {}) {
      const { force = false } = options
      this._setModuleLoading('content', true)

      const fetchOptions = force ? { forceRefresh: true } : {}

      if (force) {
        ['content', 'imprint', 'data_protection', 'terms'].forEach((table) => {
          this.apiService.invalidateTableCache(table)
        })
      }
      
      try {
        const result = await this.apiService.getCollection('content', fetchOptions)
        const items = (result.data || []).filter(
          (item) => !LEGAL_CONTENT_IDENTIFIERS.has(item.identifier)
        )

        for (const { table, identifier, title } of [
          { table: 'imprint', identifier: 'imprint', title: 'Impressum' },
          { table: 'data_protection', identifier: 'data_protection', title: 'Datenschutzerklärung' },
          { table: 'terms', identifier: 'terms', title: 'Allgemeine Geschäftsbedingungen (AGB)' }
        ]) {
          try {
            const legal = await this.apiService.getCollection(table, {
              limit: 20,
              ...fetchOptions
            })
            const item = pickLegalContentItem(legal.data, identifier, title)
            if (item) {
              items.push(item)
            }
          } catch (legalError) {
            console.warn(`[API Store] ${table} unavailable:`, legalError.message)
          }
        }

        this._setModuleData('content', items, true)
        return { ...result, data: items }
      } catch (error) {
        this._setModuleError('content', error.message)
        throw error
      } finally {
        this._setModuleLoading('content', false)
      }
    },

    /**
     * Lädt General Images (Impressionen)
     */
    async loadImpressions() {
      this._setModuleLoading('impressions', true)
      
      try {
        const result = await this.apiService.getCollection('general_images')
        this._setModuleData('impressions', result.data, true)
        return result
      } catch (error) {
        this._setModuleError('impressions', error.message)
        throw error
      } finally {
        this._setModuleLoading('impressions', false)
      }
    },

    /**
     * Lädt Gallery mit Apartment-Relationen
     */
    async loadGallery() {
      this._setModuleLoading('gallery', true)
      
      try {
        const result = await this.apiService.getWithRelations('gallery', [
          {
            field: 'vacation_rental_id',
            from: 'vacation_rentals',
            as: 'vacation_rental'
          }
        ])
        this._setModuleData('gallery', result.data, true)
        return result
      } catch (error) {
        this._setModuleError('gallery', error.message)
        throw error
      } finally {
        this._setModuleLoading('gallery', false)
      }
    },

    /**
     * Lädt Sections
     */
    async loadSections() {
      this._setModuleLoading('sections', true)
      
      try {
        const result = await this.apiService.getCollection('sections')
        this._setModuleData('sections', result.data, true)
        return result
      } catch (error) {
        this._setModuleError('sections', error.message)
        throw error
      } finally {
        this._setModuleLoading('sections', false)
      }
    },

    /**
     * Lädt Parallax Images
     */
    async loadParallaxImages() {
      this._setModuleLoading('parallaxImages', true)
      
      try {
        const result = await this.apiService.getCollection('parallax_images')
        this._setModuleData('parallaxImages', result.data, true)
        return result
      } catch (error) {
        this._setModuleError('parallaxImages', error.message)
        throw error
      } finally {
        this._setModuleLoading('parallaxImages', false)
      }
    },

    /**
     * Lädt Background Images
     */
    async loadBackgroundImages() {
      this._setModuleLoading('backgroundImages', true)
      
      try {
        const result = await this.apiService.getCollection('background_images')
        this._setModuleData('backgroundImages', result.data, true)
        return result
      } catch (error) {
        this._setModuleError('backgroundImages', error.message)
        throw error
      } finally {
        this._setModuleLoading('backgroundImages', false)
      }
    },

    _normalizeAvailabilityEntries(items, apartmentRef = null) {
      if (!Array.isArray(items)) return []

      return items
        .map((item) => {
          const start = item.start || item.startDate || item.checkIn || item.check_in || item.begin || item.from || item.start_date || item.date || item.bookingStart || item.arrival
          const end = item.end || item.endDate || item.checkOut || item.check_out || item.until || item.to || item.end_date || item.bookingEnd || item.departure
          const title = item.title || item.name || item.description || item.summary || item.label || item.buchungsNr || item.bookingNumber || item.booking_number || 'Verfügbarkeit'
          const status = item.status || item.state || item.type || item.availability || item.kind

          return {
            ...item,
            title,
            start,
            end,
            status,
            availabilityKind: categorizeBookingStatus(status)
          }
        })
        .filter((item) => item.start || item.end)
        .filter((item) => !isNonBlockingBookingStatus(item.status))
        .filter((item) => !apartmentRef || this._matchesApartmentReference(item, apartmentRef))
    },

    _normalizeText(value) {
      if (value == null) return ''
      return String(value)
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },

    _collectApartmentTokens(value, target) {
      if (value == null) return

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        const normalized = this._normalizeText(value)
        if (normalized) target.push(normalized)
        return
      }

      if (Array.isArray(value)) {
        value.forEach((entry) => this._collectApartmentTokens(entry, target))
        return
      }

      if (typeof value === 'object') {
        const apartmentFieldNames = [
          'ferienwohnungid',
          'ferienwohnung_id',
          'vacationrentalid',
          'vacation_rental_id',
          'apartmentid',
          'apartment_id',
          'vacationrental',
          'vacation_rental',
          'ferienwohnung',
          'apartment',
          'property',
          'unit',
          'slug',
          'name',
          'title',
          'identifier',
          'code',
          'id',
          '_id'
        ]

        Object.entries(value).forEach(([key, entry]) => {
          const normalizedKey = this._normalizeText(key)
          if (apartmentFieldNames.includes(normalizedKey) || normalizedKey.includes('wohnung') || normalizedKey.includes('apartment') || normalizedKey.includes('vacation') || normalizedKey.includes('slug') || normalizedKey.includes('name') || normalizedKey.includes('title')) {
            this._collectApartmentTokens(entry, target)
          }
        })
      }
    },

    _matchesApartmentReference(item, apartmentRef) {
      if (!apartmentRef) return true

      const candidateValues = []
      const itemValues = []

      this._collectApartmentTokens(apartmentRef, candidateValues)
      this._collectApartmentTokens(item, itemValues)

      if (!candidateValues.length) return true
      if (!itemValues.length) return false

      return candidateValues.some((candidate) =>
        itemValues.some((value) => value === candidate || value.includes(candidate) || candidate.includes(value))
      )
    },

    _parseAvailabilityBoundary(value) {
      if (!value) return null
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return null
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    },

    _buildAvailabilityDateFilter(startDate, endDate) {
      if (!startDate || !endDate) return {}

      const rangeStart = this._parseAvailabilityBoundary(startDate)
      const rangeEnd = this._parseAvailabilityBoundary(endDate)
      if (!rangeStart || !rangeEnd) return {}

      const startISO = rangeStart.toISOString()
      const endISO = new Date(
        rangeEnd.getFullYear(),
        rangeEnd.getMonth(),
        rangeEnd.getDate(),
        23,
        59,
        59,
        999
      ).toISOString()

      // Overlap filter for haus_birkengruen bookings (API stores BSON Date after migration).
      return {
        check_in: { $lte: endISO },
        check_out: { $gte: startISO }
      }
    },

    _filterByDateRange(items, startDate, endDate) {
      if (!startDate || !endDate) return items

      const rangeStart = this._parseAvailabilityBoundary(startDate)
      const rangeEnd = this._parseAvailabilityBoundary(endDate)
      if (!rangeStart || !rangeEnd) return items

      return items.filter((item) => {
        const bookingStart = this._parseAvailabilityBoundary(item.start)
        const bookingEnd = this._parseAvailabilityBoundary(item.end)
        if (!bookingStart || !bookingEnd) return false

        return bookingStart <= rangeEnd && bookingEnd >= rangeStart
      })
    },

    _buildApartmentFilter(apartmentRef) {
      if (!apartmentRef || typeof apartmentRef !== 'object') return null

      const apartmentId = apartmentRef._id || apartmentRef.id || apartmentRef.vacation_rental_id
      if (apartmentId) {
        return { vacation_rental_id: apartmentId }
      }

      const slug = apartmentRef.slug
      if (slug) {
        return {
          $or: [
            { slug },
            { apartment_slug: slug },
            { vacation_rental_slug: slug }
          ]
        }
      }

      return null
    },

    // loadAvailability: optionally accepts an options object { startDate, endDate }
    // to request only availability that overlaps the provided date range.
    async loadAvailability(apartmentRef = null, options = {}) {
      const requestToken = ++this.availabilityRequestToken
      this._setModuleLoading('availability', true)
      this._setModuleData('availability', [], true)

      try {
        const candidateTables = [
          'bookings',
          'buchungen',
          'booking',
          'calendar',
          'calendar_events',
          'calendars',
          'availability',
          'availability_calendar',
          'reservations',
          'reservation',
          'events'
        ]

        for (const tableName of candidateTables) {
          try {
            // Build a filter that excludes cancelled bookings and, if provided,
            // restricts results to entries overlapping the requested date range.
            const { startDate, endDate } = options || {}
            const filterObj = {}
            const apartmentFilter = this._buildApartmentFilter(apartmentRef)

            if (apartmentFilter) {
              Object.assign(filterObj, apartmentFilter)
            }

            Object.assign(filterObj, this._buildAvailabilityDateFilter(startDate, endDate))

            const filterOption = {
              limit: 100,
              page: 1,
              _cacheBust: Date.now(),
              ...(Object.keys(filterObj).length > 0 ? { filter: JSON.stringify(filterObj) } : {})
            }
            const result = await this.apiService.getCollection(tableName, filterOption)
            const normalized = this._filterByDateRange(
              this._normalizeAvailabilityEntries(result?.data || [], apartmentRef),
              startDate,
              endDate
            )

            if (normalized.length > 0) {
              if (requestToken !== this.availabilityRequestToken) return []
              this._setModuleData('availability', normalized, true)
              return normalized
            }
          } catch (error) {
            console.warn(`[API Store] Availability table ${tableName} unavailable:`, error.message)
          }
        }

        if (requestToken !== this.availabilityRequestToken) return []
        this._setModuleData('availability', [], true)
        return []
      } catch (error) {
        if (requestToken !== this.availabilityRequestToken) return []
        this._setModuleError('availability', error.message)
        throw error
      } finally {
        if (requestToken === this.availabilityRequestToken) {
          this._setModuleLoading('availability', false)
        }
      }
    },

    /**
     * Erstellt eine neue Bewertung
     */
    async createRating(ratingData) {
      try {
        const vacationRentalId =
          ratingData.vacation_rental_id || ratingData.apartments_id

        if (!vacationRentalId) {
          throw new Error('Ferienwohnung fehlt für die Bewertung.')
        }

        const reviewPayload = {
          guest_name: ratingData.name || ratingData.guest_name,
          review_text: ratingData.comment || ratingData.review_text,
          rating: Number(ratingData.rating),
          vacation_rental_id: vacationRentalId,
          date: new Date().toISOString(),
          published: false
        }

        const result = await this.apiService.createItem('reviews', reviewPayload)
        await this.loadReviews()
        return result
      } catch (error) {
        console.error('Failed to create rating:', error)
        throw error
      }
    },

    async _findGuestByEmail(email) {
      const normalizedEmail = normalizeEmail(email)
      if (!normalizedEmail) return null

      const result = await this.apiService.getCollection('guests', {
        filter: JSON.stringify({ email: normalizedEmail }),
        limit: 5,
        page: 1
      })

      const matches = (result?.data || []).filter(
        (guest) => normalizeEmail(guest.email) === normalizedEmail
      )

      return matches[0] || null
    },

    async resolveOrCreateGuest(payload) {
      const guestData = buildGuestPayload(payload)
      const existingGuest = await this._findGuestByEmail(guestData.email)

      if (existingGuest?._id) {
        const updates = buildGuestUpdatePayload(existingGuest, payload)
        if (Object.keys(updates).length > 0) {
          await this.apiService.updateItem('guests', existingGuest._id, updates)
        }
        return existingGuest._id
      }

      const created = await this.apiService.createItem('guests', guestData)
      const guestId = created?.data?._id || created?.data?.insertedId
      if (!guestId) {
        throw new Error('Gast konnte nicht angelegt werden.')
      }
      return guestId
    },

    async checkBookingSlotAvailable(apartment, payload) {
      const apartmentId = getApartmentId(apartment)
      const checkIn = buildCheckInDateTime(payload.check_in)
      const checkOut = buildCheckOutDateTime(payload.check_out)

      if (!apartmentId || !checkIn || !checkOut) {
        return { available: false, conflicts: [] }
      }

      const filterObj = {
        ...this._buildApartmentFilter(apartment),
        ...this._buildAvailabilityDateFilter(payload.check_in, payload.check_out)
      }

      const result = await this.apiService.getCollection('bookings', {
        filter: JSON.stringify(filterObj),
        limit: 100,
        page: 1,
        _cacheBust: Date.now()
      })

      const conflicts = (result?.data || []).filter((item) => {
        if (!isBlockingBookingStatus(item.status)) return false

        const existingStart = new Date(item.check_in || item.start)
        const existingEnd = new Date(item.check_out || item.end)

        return bookingRangesOverlap(existingStart, existingEnd, checkIn, checkOut)
      })

      return {
        available: conflicts.length === 0,
        conflicts
      }
    },

    async submitBookingRequest(apartment, payload) {
      if (!this.apiService || !this.myportalClient) {
        throw new Error('API ist nicht initialisiert.')
      }

      const validationErrors = validateBookingRequest(payload, apartment)
      if (validationErrors.length > 0) {
        throw new Error(validationErrors[0])
      }

      const apartmentId = getApartmentId(apartment)
      if (!apartmentId) {
        throw new Error('Ferienwohnung konnte nicht zugeordnet werden.')
      }

      const availability = await this.checkBookingSlotAvailable(apartment, payload)

      if (!availability.available) {
        throw new Error('Der gewählte Zeitraum ist leider nicht verfügbar.')
      }

      const guestId = await this.resolveOrCreateGuest(payload)
      const bookingData = buildBookingPayload({
        apartmentId,
        guestId,
        payload
      })

      const booking = await this.apiService.createItem('bookings', bookingData)
      const bookingId = booking?.data?._id || booking?.data?.insertedId

      return {
        bookingId,
        guestId,
        bookingNumber: bookingData.booking_number
      }
    },

    /**
     * Cache-Management
     */
    clearAvailability() {
      this.availabilityRequestToken += 1
      this._setModuleData('availability', [], true)
      this._setModuleError('availability', null)
      this._setModuleLoading('availability', false)
    },

    clearCache() {
      if (this.apiService) {
        this.apiService.clearCache()
      }
    },

    async sendContactMessage(mailTo, form) {
      if (!this.myportalClient) {
        throw new Error('API ist nicht initialisiert.')
      }

      await sendContactMessage(this.myportalClient, mailTo, form)
    },

    getCacheStats() {
      return this.apiService ? this.apiService.getCacheStats() : {}
    }
  }
})